import { Dayjs } from 'dayjs'

/** The registration window as edited by the four date/time fields. */
export type RegistrationWindowChange = {
  startsAt: Dayjs | null
  endsAt: Dayjs | null
}

/** Which end of the window an edit applies to. */
export type WindowBoundary = 'start' | 'end'

/**
 * Seeds the time on a freshly picked date so the window covers whole days:
 * midnight for the start, one second to midnight for the end. Once a time
 * exists it is kept — only the calendar day moves.
 *
 * @param picked - the date the admin just chose, or null when cleared
 * @param previous - the datetime already stored for this end, if any
 * @param boundary - which end of the window is being set
 */
export const withDefaultTime = (
  picked: Dayjs | null,
  previous: Dayjs | null,
  boundary: WindowBoundary
): Dayjs | null => {
  if (!picked) {
    return null
  }

  if (previous) {
    return picked
      .hour(previous.hour())
      .minute(previous.minute())
      .second(previous.second())
      .millisecond(0)
  }

  // endOf('day') carries .999ms; drop it so every value this module produces is
  // whole-second, which keeps later isBefore/isSame comparisons predictable.
  return boundary === 'start'
    ? picked.startOf('day')
    : picked.endOf('day').millisecond(0)
}

/**
 * Keeps the window in order after an edit to one end.
 *
 * The edited end is always honoured — the admin just chose it — so when the two
 * ends invert it is the *other* one that gets cleared, leaving the form
 * incomplete rather than silently holding an impossible window. Compared as
 * full datetimes so a same-day end at an earlier clock time is caught too,
 * which a day-granularity `disabledDate` cannot see.
 *
 * @param next - the window as it would be after the edit
 * @param edited - which end the admin just changed
 */
export const withOrderedWindow = (
  next: RegistrationWindowChange,
  edited: WindowBoundary
): RegistrationWindowChange => {
  const { startsAt, endsAt } = next

  if (!startsAt || !endsAt || !endsAt.isBefore(startsAt)) {
    return next
  }

  return edited === 'start'
    ? { startsAt, endsAt: null }
    : { startsAt: null, endsAt }
}
